import { BackendUpdatePayloadTypes } from '@freelancer/datastore/backend.model';
import {
  assertNever,
  Backend,
  getQueryParamValue,
  objectKeysFilter,
  OrderByDirection,
  setDiff,
  toNumber,
} from '@freelancer/datastore/core';
import {
  ProjectStatusApi,
  ProjectUpgradeOptionsApi,
} from 'api-typings/projects/projects';
import { ProjectAction } from './projects.backend-model';
import { ProjectUpgrades } from './projects.model';

export function projectsBackend(): Backend<'projects'> {
  return {
    defaultOrder: {
      field: 'timeSubmitted',
      direction: OrderByDirection.DESC,
    },
    fetch: (authUid, ids, query, order) => ({
      endpoint: `projects/0.1/projects`,
      params: {
        projects: [
          ...(ids || []).map(toNumber),
          ...getQueryParamValue(query, 'id'),
        ],
        owners: getQueryParamValue(query, 'ownerId'),
        seo_urls: getQueryParamValue(query, 'seoUrl'),
        frontend_project_statuses: getQueryParamValue(
          query,
          'frontendProjectStatus',
        ),
        selected_bids: 'true', // For project status. TODO: remove once T69699 is fixed
        file_details: 'true',
      },
    }),

    update: (authUid, entity, originalEntity) => {
      let payload: BackendUpdatePayloadTypes['projects'] | undefined;
      const method = 'PUT';
      const endpoint = `projects/0.1/projects/${originalEntity.id}/`;

      if (entity.status === ProjectStatusApi.CLOSED) {
        payload = {
          action: ProjectAction.CLOSE,
        };
      }

      // Updating upgrades.
      if (entity.upgrades) {
        if (payload) {
          throw new Error(`Cannot update two fields at once`);
        }

        const unconvertedUpgrades = setDiff(
          objectKeysFilter(entity.upgrades, x => x),
          // refer to comments in project-view-project.backend.ts
          objectKeysFilter(
            originalEntity.upgrades,
            (val, key) => key !== 'extend' && val,
          ),
        );

        const upgrades = unconvertedUpgrades.map(convertUpgrade);

        payload = {
          action: ProjectAction.UPGRADE,
          'upgrades[]': upgrades,
        };
      }

      if (payload) {
        return {
          payload,
          endpoint,
          method,
        };
      }

      throw new Error(`Cound not update any fields of the project`);
    },

    remove: (authUid, projectId, originalObject) => {
      const method = 'DELETE';
      const endpoint = `projects/0.1/projects/${projectId}`;
      const payload = {};
      return {
        payload,
        endpoint,
        method,
      };
    },
  };
}

export function convertUpgrade(
  upgrade: keyof ProjectUpgrades,
): ProjectUpgradeOptionsApi {
  switch (upgrade) {
    case 'featured':
      return ProjectUpgradeOptionsApi.FEATURED;
    case 'assisted':
      return ProjectUpgradeOptionsApi.RECRUITER;
    case 'extend':
      return ProjectUpgradeOptionsApi.EXTEND;
    case 'urgent':
      return ProjectUpgradeOptionsApi.URGENT;
    case 'nonpublic':
      return ProjectUpgradeOptionsApi.NONPUBLIC;
    case 'fulltime':
      return ProjectUpgradeOptionsApi.FULLTIME;
    case 'listed':
      throw new Error('You cannot upgrade to listed');
    case 'NDA':
      return ProjectUpgradeOptionsApi.NDA;
    case 'ipContract':
      return ProjectUpgradeOptionsApi.IP_CONTRACT;
    case 'sealed':
      return ProjectUpgradeOptionsApi.HIDEBIDS;
    // case 'successBundle':
    //   return ProjectUpgradeOptionsApi.SUCCESS_BUNDLE;
    // case 'nonCompete':
    //   return ProjectUpgradeOptionsApi.NON_COMPETE;
    case 'projectManagement':
      return ProjectUpgradeOptionsApi.PROJECT_MANAGEMENT;
    // case 'pfOnly':
    //   return ProjectUpgradeOptionsApi.PF_ONLY;
    case 'qualified':
      throw new Error('You cannot upgrade to qualified');
    default:
      return assertNever(upgrade);
  }
}
